/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* global __IS_DROPIN__ */
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import { getSHA256Digest } from "utils/DataEncoding";
import { analytics } from "dc-core";
import * as stores from "stores";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { getFileStorageType, getFileShareType, getAssetPageCount } from "selectors/documentSelectors";
import { getConnectorType } from "app/AppInitUtility";
import docApi from "api";
import { isSignAsset } from "utils/AssetUtils";
import { SING_ASSET, VIEWER_DOCUMENT_ID } from "constants/constants";
import { getBrowser } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { getOS, getDeviceInfo } from "dc-preview-utils/src/Utils/DeviceUtils";

let emitEvent;
let documentIdHash = "";
let documentIdHashPromise;

emitEvent = analytics.getEventSender("dcpreviewdropin");

let documentId = "";
let previewScopedDocumentId = false;

export const isAnalyticsLoggingEnabled = () =>
  getQueryParameterFromUrl(window.location.href, "enableAnalytics") !== "false";

const emitEventHelper = (event, custom, content, session, verb, opts) => {
  emitEvent(
    {
      event,
      custom,
      content,
      session,
      verb
    },
    opts // {requestType: string, enableLogging: boolean, enableAnalytics:boolean, queueEvent: boolean}
  );
};

export const sendEvent = (
  workflow,
  subcategory,
  type,
  contextData = {},
  value,
  opts = false,
  session = {},
  verb = {},
  contentData = {}
) => {
  // check if we need to analytics logging based on url flag
  if (isAnalyticsLoggingEnabled()) {
    const event = {
      workflow,
      subcategory,
      type
    };
    if (value) {
      event.value = value;
    }

    const custom = contextData;
    if (!custom["adb.event.context.dcweb.clickcontext"]) {
      const activeContext = getQueryParameterFromUrl(window.location.href, "viewer!megaVerb");
      custom["adb.event.context.dcweb.clickcontext"] =
        activeContext || (getShouldUseModernViewer(stores) ? "Viewer" : "Preview");
    }
    const fileStorageType = getFileStorageType(stores);
    const fileShareType = getFileShareType(stores);
    const _isSignAsset = isSignAsset(docApi.getAssetType());

    if (!custom["adb.event.context.sharing.type"]) {
      if (_isSignAsset) {
        custom["adb.event.context.sharing.type"] = SING_ASSET;
      } else if (fileShareType) custom["adb.event.context.sharing.type"] = fileShareType;
    }

    const connectorType = getConnectorType();
    const content = contentData;
    if (connectorType) {
      content.source = connectorType;
    } else if (fileStorageType) {
      content.source = fileStorageType;
    } else if (_isSignAsset) {
      content.source = "sign";
    }

    documentIdHashPromise
      .then(hashValue => {
        documentIdHash = hashValue;
      })
      .catch(() => {
        documentIdHash = "";
      })
      .then(async () => {
        if (documentIdHash) {
          custom["adb.event.context.pdfviewer.documentId"] = documentIdHash;
          custom["adb.event.context.pdfviewer.browser"] = getBrowser();
          custom["adb.event.context.pdfviewer.os"] = getOS() && getOS().name;
          custom["adb.event.context.pdfviewer.device"] = getDeviceInfo();
          try {
            const pageCount = await getAssetPageCount(stores);
            pageCount && (custom["adb.event.context.pdfviewer.page_count"] = pageCount);
          } catch (err) {
            console.error("Error in getting page count", err);
          }
        }
        emitEventHelper(event, custom, content, session, verb, opts);
      });
  }
};

export const setDocumentId = docId => {
  documentId = docId;
  documentIdHashPromise = getSHA256Digest(documentId).then(docId => {
    try {
      if (!sessionStorage.getItem(VIEWER_DOCUMENT_ID)) {
        sessionStorage.setItem(VIEWER_DOCUMENT_ID, docId);
        previewScopedDocumentId = true;
      }
    } catch (ex) {
      console.log("Session Storage Access is Denied");
    }
    return docId;
  });
};

export const resetDocumentId = docId => {
  documentId = "";
  documentIdHashPromise = new Promise(() => {});
  try {
    if (previewScopedDocumentId) {
      sessionStorage.removeItem(VIEWER_DOCUMENT_ID);
      previewScopedDocumentId = false;
    }
  } catch (ex) {
    console.log("Session Storage Access is Denied");
  }
};

export const getDocumentId = () => documentIdHashPromise;
